import { observer } from 'mobx-react-lite';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import { displayMoney, toStringTwoDecimals } from '../../utils/money.utils';
import { usePrevious } from '../../hooks/usePrevious';
import { runInAction, toJS } from 'mobx';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { EventDates } from './components/EventDates';
import { EventLocation } from './components/EventLocation';
import Asterisk from '../../components/Asterisk/Asterisk';
import EventLabel from '../../components/EventLabel/EventLabel';
import { getUrlParam } from '../../utils/url.utils';
import _ from 'lodash';
import EventQuantizer from './components/EventQuantizer';
import Spinner from '../../components/Spinner';
import { basedAxios } from '../../store/Store';
import cx from 'classnames';
import { makeStyles } from '@material-ui/core';
//import { EventDetails } from './components/EventDetails';
//import { YesNoSelector } from './components/YesNoSelector';

const useStyles = makeStyles(theme => ({
  checkbox: {
    '&:hover': {
      backgroundColor: 'transparent', // Remove hover background color
    },
    '&.Mui-checked:hover': {
      backgroundColor: 'transparent', // Remove hover background color when checked
    },
  },
}));

const getStatusLabel = (isFullBooked, isNoVouchersAvailable, isDue) => {
  if (isFullBooked) {
    return <EventLabel name="FullBooked" groupName="CMS.FormEvent" />;
  } else if (isNoVouchersAvailable) {
    return <EventLabel name="NoVouchersAvailable" groupName="CMS.FormEvent" />;
  } else if (isDue) {
    return <EventLabel name="EventDue" groupName="CMS.FormEvent" />;
  }
  return '';
};

export const Event = observer(props => {
  const [isFetching, setIsFetching] = useState(false);
  const [isFullBooked, setIsFullbooked] = useState(false);
  const [isNoVouchersAvailable, setIsNoVouchersAvailable] = useState(false);
  const [isReadonly, setIsReadonly] = useState(false);
  const [isFetchingVouchers, setIsFetchingVouchers] = useState(false);

  const classes = useStyles();

  const {
    settings,
    environment,
    breakouts,
    contentLayoutModuleId,
    displayEventSettings,
    checkConditions,
    checkConditionsOld,
    current,
    updateOrder,
    ordersLoaded,
    activePersonNo,
    contentLayout,
    validations,
    contentModuleId,
    hasParent,
    updateOrderRow,
    setBreakoutFullbooked,
    checkEventAvailability,
    validate: vdate,
    parentValid,
    setVoucherFullbooked,
  } = props;

  const orders = toJS(props.orders);

  const {
    eventId,
    readonly,
    hidden,
    prechecked,
    allowOverbooking,
    required,
    overrideDisplay,
    displayType = 'checkbox',
    displayYesNo = false,
    voucherCheck,
    showAlternativeName = false,
    checkOnVisible = false,
    disableChangeLock,
    showQuantity = false,
    cancelOnUncheck = false,
    rebookable = false,
    hideOnFullbooked = false,
  } = settings;

  const validity = validations.find(v => v.id == contentModuleId);
  const prevProps = usePrevious(props);

  //const order = orders[activePersonNo];
  const order = orders?.[activePersonNo];
  const breakout = breakouts.find(q => q.eventId === eventId);

  const eventRejected = useMemo(
    () => Boolean(order?.orderExtra?.find(oe => oe.name === 'EventRejected' && oe.value == eventId)),
    [order, order?.orderExtra, eventId]
  );

  const activeDisplaySettings = useMemo(() => {
    if (!overrideDisplay) {
      return displayEventSettings;
    }

    return Object.keys(displayEventSettings).reduce((o, key) => {
      if (Object.hasOwnProperty.call(displayEventSettings, key)) {
        return { ...o, [key]: settings[_.camelCase(key)] === true };
      }
      return o;
    }, {});
  }, [overrideDisplay, displayEventSettings, settings]);

  if (!breakout) return null;

  const { name, startDateTime, endDateTime, description, amount, vatPercent, eventDisplayLanguage, deadline } = breakout;

  const displayLang = eventDisplayLanguage.find(x => x.languageCode === environment.lang && x.alternative === showAlternativeName);
  const isDue = useMemo(() => (deadline ? +new Date(deadline) < +new Date() : false), [deadline]);

  const quantity = useMemo(() => {
    const row = order?.orderRow?.find(or => or.eventId == breakout.eventId);
    return row?.quantity ?? 0;
  }, [breakout, order?.orderRow]);

  const handleQuantityChange = useCallback(
    qnty => {
      const orderRow = order?.orderRow?.find(or => or.eventId == breakout.eventId);

      if (qnty < 1 && orderRow) {
        handleCheckboxChange(false, false); // remove event from order
      } else if (qnty === 1 && !orderRow) {
        handleCheckboxChange(true, false); // add event to order
      } else if (qnty > 1 && !orderRow) {
        // update quantity
        handleCheckboxChange(true, false, qnty);
      } else if (qnty > 1 && orderRow) {
        updateOrderRow({
          ...orderRow,
          quantity: qnty,
        });
      }
      runInAction(() => {
        props.userInteracted = true;
      });
    },
    [breakout, order, updateOrderRow]
  );

  useEffect(() => {
    // Is the order loaded?
    if (ordersLoaded) {
      // Is the event prechecked?
      if (prechecked) {
        // Is the event hidden?w        // Is the event hidden?
        if (hidden) {
          // If the event is hidden, we need to check if it should be checked
          if (!checkOnVisible) {
            checkAvailableStatus(true, true);
          } else {
            //If hidden remove the event from the order
            checkAvailableStatus(false, true);
          }
        } else {
          checkAvailableStatus(true, true);
        }
      } else {
        // availability
        // Only delete the event if the user has interacted with the modules
        if (hidden && props.userInteracted) {
          //If hidden remove the event from the order
          checkAvailableStatus(false, true);
        } else {
          checkAvailableStatus();
        }
      }
    }
  }, [prechecked, hidden, checkOnVisible, ordersLoaded, activePersonNo]);

  useEffect(() => {
    validate();
    checkForOverbooking();
  }, [order, order?.orderRow, isFullBooked]);

  const validate = useCallback(() => {
    let row = order?.orderRow?.find(or => or.eventId == breakout.eventId);
    let isValid = true;
    let reason = '';

    if (isDue) {
      isValid = true;
      reason = 'due';
    } else if (!isFullBooked && (readonly || hidden)) {
      isValid = true;
      return isValid;
    } else if (required) {
      if (displayYesNo) {
        if ((!row || row?.deleted) && !eventRejected) {
          isValid = false;
          reason = 'rejected';
        }
      } else if (!row || row?.deleted) {
        isValid = false;
        reason = 'required';
      }
    }
    if (hasParent && typeof props.validateGroup === 'function') {
      props.validateGroup({ isValid, reason });
    } else if (validity) {
      runInAction(() => {
        validity.valid = isValid;
        validity.reason = reason;
      });
    }
  }, [isFullBooked, readonly, hidden, required, isDue, eventRejected, displayYesNo, hasParent, validity, order, breakout.eventId]);

  const checkForOverbooking = () => {
    const events = contentLayout.contentLayoutModules.filter(fe => {
      return fe.contentModule && fe.contentModule.type === 'EVENT' && fe.contentModule.settings.eventId !== breakout.eventId;
    });

    events.map(e => {
      if (!allowOverbooking && e.pages === current.page) {
        let matchedEvent = breakouts.find(q => q.eventId == e.contentModule.settings.eventId);
        if (matchedEvent) {
          if (
            (+new Date(matchedEvent.startDateTime) > +new Date(startDateTime) && +new Date(matchedEvent.startDateTime) < +new Date(endDateTime)) ||
            (+new Date(matchedEvent.endDateTime) > +new Date(startDateTime) && +new Date(matchedEvent.endDateTime) <= +new Date(endDateTime))
          ) {
            setIsReadonly(true);
          }
        }
      }
    });
  };

  const handleCheckboxChange = (value, autoCheck, qnty = null) => {
    if (readonly && !autoCheck) return;
    if (value === undefined) return;
    if (props.hasParent && settings.onlySelectOne) {
      props.uncheckGroupActivities(breakout.eventId);
    }

    let update = true;

    if (
      (autoCheck && settings.prechecked && !checkOnVisible && order?.orderRow?.filter(x => x.eventId === breakout.eventId).length > 0) ||
      (value === false && autoCheck && settings.prechecked && checkOnVisible && order?.orderRow?.filter(x => x.eventId === breakout.eventId).length === 0)
    ) {
      update = false;
    }

    if (update) {
      const deleted = cancelOnUncheck && order?.badgeNo && !value ? false : !value;
      const status =
        cancelOnUncheck && order?.badgeNo && !value ? 'Cancelled' : order?.orderRow?.find(or => or.eventId === breakout.eventId)?.orderRowId ? 'Booked' : null;
      updateOrderRow(
        {
          amount: breakout.amount ?? 0,
          comment: '',
          discount: 0,
          eventId: breakout.eventId,
          orderId: order?.orderId ?? -1,
          orderRowId: order?.orderRow?.find(or => or.eventId === breakout.eventId)?.orderRowId ?? ((order?.orderRow?.length ?? 0) + 1) * -1,
          quantity: qnty ?? 1,
          status,
          vatPercent: breakout.vatPercent || 0,
          deleted,
          groupId: props.hasParent ? props.parentId : null,
        },
        !value,
        autoCheck,
        displayYesNo
      );
    }
    if (hasParent) {
      checkConditionsOld();
      checkConditions();
    } else {
      checkConditionsOld(contentLayoutModuleId);
      checkConditions(`EVENT.${breakout.eventId}`);
    }
  };

  const handleWaitlist = async value => {
    if (!breakout.waitlist) return;

    updateOrderRow(
      {
        amount: breakout.amount ?? 0,
        comment: '',
        discount: 0,
        eventId: breakout.eventId,
        orderId: order?.orderId ?? -1,
        orderRowId: order?.orderRow?.find(or => or.eventId === breakout.eventId)?.orderRowId ?? ((order?.orderRow?.length ?? 0) + 1) * -1,
        quantity: 1,
        status: 'Waitlist',
        vatPercent: breakout.vatPercent || 0,
        deleted: !value,
        groupId: props.hasParent ? props.parentId : null,
      },
      !value ? true : false,
      false,
      displayYesNo
    );

    if (hasParent) {
      checkConditionsOld();
      checkConditions();
    } else {
      checkConditionsOld(contentLayoutModuleId);
      checkConditions(`EVENT.${breakout.eventId}`);
    }
  };

  const checkAvailableStatus = async (value, preChecked = false) => {
    if (value === false) {
      if (breakout.waitlist && order?.orderRow?.find(or => or.eventId === breakout.eventId)?.status === 'Waitlist') {
        return handleWaitlist(false);
      }

      return handleCheckboxChange(false, preChecked);
    }

    if (isFetching === false) {
      setIsFetching(true);
      const available = await checkEventAvailability(
        breakout.eventId,
        orders.length > 0 && orders[activePersonNo].orderId ? orders[activePersonNo].orderId : null,
        props.sqlStateNo,
        orders[activePersonNo]?.orderRow.some(orow => orow.eventId === breakout.eventId) ? false : true
      );

      if (available) {
        checkVoucherAvailable(value, preChecked);
        setIsFullbooked(false);
      } else {
        if (breakout.waitlist && value === true) {
          handleWaitlist(true);
        }
        setBreakoutFullbooked(breakout.eventId, true);
        setIsFullbooked(true);
      }
      setIsFetching(false);
    }
  };

  const checkVoucherAvailable = (value, preChecked = false) => {
    let exhibitorId = order?.orderExtra?.find(x => x.name == 'ExhibitorInvite')?.value;
    let personTypes = order?.personTypes?.map(x => x.personTypeId) ?? [];

    if (!exhibitorId && getUrlParam('exi')) {
      exhibitorId = parseInt(getUrlParam('exi'));
    }

    if (getUrlParam('pti') && (personTypes.length === 0 || !personTypes.some(x => x == getUrlParam('pti')))) {
      personTypes.push(parseInt(getUrlParam('pti')));
    }
    if (!exhibitorId || !voucherCheck) {
      handleCheckboxChange(value, preChecked);
    } else {
      if (isFetchingVouchers === false) {
        setIsFetchingVouchers(true);

        basedAxios
          .get(
            `./exhibitor/availableVouchers/${breakout.eventId}${
              orders.length > 0 && orders[activePersonNo].orderId ? '/' + orders[activePersonNo].orderId : ''
            }?sqlStateNo=${props.sqlStateNo}&exi=${exhibitorId}&ptis=${personTypes.join(',')}`
          )
          .then(({ data }) => {
            if (data.available) {
              handleCheckboxChange(value, preChecked);
              setIsNoVouchersAvailable(false);
            } else {
              setIsNoVouchersAvailable(true);
              setVoucherFullbooked(breakout.eventId, true);
            }
            setIsFetchingVouchers(false);
          })
          .catch(err => {
            setIsFetchingVouchers(false);
            console.error(err);
          });
      }
    }
  };

  const price = (asNumber = false) => {
    let discountAmount = 0;

    let discounts = order?.discounts || [];
    if (order && order.orderId > 0 && order?.orderRow?.find(o => o.eventId == breakout.eventId)) {
      discountAmount = order?.orderRow?.find(o => o.eventId == breakout.eventId)?.discount;
    }

    discounts
      .filter(d => (d.targetEventId == breakout.eventId && d.value != 0) || (d.targetEventId == null && d.valueType == 'Percent' && d.value != 0))
      .sort((a, b) => {
        return a.value - b.value;
      })
      .map(d => {
        discountAmount += d.valueType == 'Percent' ? (amount - discountAmount) * (d.value / 100) : d.value;
      });

    const oldPrice = parseFloat(activeDisplaySettings['ShowEventIncVat'] == true ? displayMoney(parseFloat(amount), vatPercent) : amount);

    const price = parseFloat(
      activeDisplaySettings['ShowEventIncVat'] ? displayMoney(parseFloat(amount - discountAmount), vatPercent) : amount - discountAmount
    );

    if (showQuantity && quantity > 1) {
      return (
        <>
          <p>{`${toStringTwoDecimals(oldPrice * quantity)} ${environment.currency}`}</p>
          <p className="text-xs text-zinc-800">
            {`${oldPrice > 0 ? toStringTwoDecimals(price) : 0} ${environment.currency}`} / &nbsp;
            <EventLabel name="QuantityShort" groupName="CMS.FormEvent" />
          </p>
        </>
      );
    }

    if (asNumber) {
      return discountAmount;
    }

    if (!discountAmount) return <p>{`${toStringTwoDecimals(price)} ${environment.currency}`}</p>;

    return (
      <>
        <p style={{ textDecoration: 'line-through' }}>{`${toStringTwoDecimals(oldPrice)} ${environment.currency}`}</p>
        <p style={{ color: '#4caf50', marginLeft: 10 }}>{`${price > 0 ? toStringTwoDecimals(price) : 0} ${environment.currency}`}</p>
      </>
    );
  };

  useEffect(() => {
    if (!orders[activePersonNo]?.badgeNo) {
      validate();
    }
  }, [orders, validate]);

  const isChangeLocked = orders?.[activePersonNo]?.badgeNo && disableChangeLock === false;
  const statusLabel = getStatusLabel(isFullBooked, isNoVouchersAvailable, isDue);
  const isChecked = Boolean(order?.orderRow?.find(or => or.eventId === breakout.eventId && !or.deleted && or.status !== 'Cancelled'));

  const isValid = useMemo(() => {
    if (isChangeLocked) return true;
    if (vdate && (validations.find(v => v.id === contentModuleId)?.valid === false || parentValid === false)) return false;
    return undefined;
  }, [vdate, validations, isChangeLocked, contentModuleId]);

  if (hidden || (hideOnFullbooked && isFullBooked)) return null;

  return (
    <>
      <div className={cx(displayYesNo ? ' btn-row' : '', !showQuantity || breakout.waitlist ? 'left-col' : 'w-36')}>
        {showQuantity && !isFullBooked && !readonly && !breakout.waitlist && (
          <EventQuantizer sqlStateNo={props.sqlStateNo} eventId={eventId} order={order} quantity={quantity} onChange={handleQuantityChange} />
        )}
        {/* {order?.orderRow?.find(or => or.eventId === breakout.eventId)?.status} */}
        {(!showQuantity || breakout.waitlist) && (
          <>
            {displayYesNo ? (
              <>
                <button
                  onClick={() => {
                    !isChecked && !isFetching && !isFetchingVouchers ? checkAvailableStatus(true) : null;
                    runInAction(() => {
                      props.userInteracted = true;
                    });
                  }}
                  disabled={isFullBooked || readonly}
                  className={`cms-button button ${isChecked ? 'active' : ''}`}
                >
                  <div className="spinner-wrap">
                    <EventLabel groupName="CMS.FormEvent" name="YesBtn" />
                    {/* {!isFetching && !isFetchingVouchers ? null : <Spinner className="custom-spinner" />} */}
                  </div>
                </button>
                &nbsp;
                <button
                  onClick={() => {
                    checkAvailableStatus(false);
                    runInAction(() => {
                      props.userInteracted = true;
                    });
                  }}
                  disabled={readonly}
                  className={`cms-button button ${!isChecked && eventRejected ? 'active' : ''}`}
                >
                  <EventLabel groupName="CMS.FormEvent" name="NoBtn" />
                </button>
              </>
            ) : !isFetching && !isFetchingVouchers ? (
              <Checkbox
                disabled={(isFullBooked && !isChecked && !breakout.waitlist) || readonly || isDue}
                onChange={e => {
                  checkAvailableStatus(e.target.checked);
                  // runInAction(() => {
                  //   props.userInteracted = true;
                  // });
                }}
                style={{
                  opacity: (statusLabel || readonly) && !breakout.waitlist ? 0.3 : 1,
                  cursor: statusLabel && !breakout.waitlist ? 'not-allowed' : 'pointer',
                }}
                icon={
                  props.hasParent && settings.displayType === 'radio' ? (
                    <RadioButtonUncheckedIcon style={{ color: isValid === false ? '#d53443' : undefined }} className="checkbox cms-checkbox" />
                  ) : (
                    <CheckBoxOutlineBlankIcon className="checkbox cms-checkbox" style={{ color: isValid === false ? '#d53443' : undefined }} />
                  )
                }
                checkedIcon={
                  props.hasParent && settings.displayType === 'radio' ? (
                    <RadioButtonCheckedIcon className="checkbox cms-checkbox" />
                  ) : (
                    <CheckBoxIcon className="checkbox cms-checkbox" />
                  )
                }
                checked={isChecked}
                className={classes.checkbox + ' checkbox'}
                name="checkedA"
              />
            ) : (
              <span className="fade-in delay-1000">
                <Spinner />
              </span>
            )}
          </>
        )}
      </div>
      <div className="center-col">
        {statusLabel && <p className="fully-booked">{statusLabel}</p>}
        {isFullBooked && breakout.waitlist && (
          <p className="fully-booked">
            <EventLabel groupName="CMS.FormEvent" name="AddToWaitlist" />
          </p>
        )}
        <div style={{ opacity: statusLabel ? 0.3 : 1 }}>
          {activeDisplaySettings['ShowEventName'] && (
            <div className="eventname flex items-center">
              {displayLang?.name ? displayLang.name : name}
              {required ? <Asterisk /> : null}
            </div>
          )}

          <div className="sub-col flex-col flex">
            <EventDates displayEventSettings={activeDisplaySettings} event={breakout} lang={environment.lang} />
            <EventLocation displayEventSettings={activeDisplaySettings} event={breakout} />
          </div>

          {activeDisplaySettings['ShowEventDescription'] ? (
            <div className="description font-normal" dangerouslySetInnerHTML={{ __html: displayLang?.description ? displayLang.description : description }} />
          ) : null}
        </div>
      </div>
      <div className="right-col price">
        {activeDisplaySettings['ShowEventPrice'] ? price() : null}
        {activeDisplaySettings['ShowEventVatSum'] ? (
          <p>
            <EventLabel name={'VatAmountLabel'} groupName={'CMS.FormEvent'} textOnly={true} />{' '}
            {`${toStringTwoDecimals(vatPercent ? amount * (vatPercent / 100) : 0)} ${environment.currency}`}
          </p>
        ) : null}
        {activeDisplaySettings['ShowEventVatPrecentage'] ? (
          <p>
            <EventLabel name={'VatPercentLabel'} groupName={'CMS.FormEvent'} textOnly={true} /> {`${vatPercent || 0}%`}
          </p>
        ) : null}
      </div>
    </>
  );

  // Updated version with more absraction and readability #abstraction-overhaul
  // return (
  //   <>
  //     <div className={cx(displayYesNo ? ' btn-row' : '', !showQuantity ? 'left-col' : 'w-36')}>{SelectorComponent}</div>
  //     <div className="center-col">
  //       {statusLabel && <p className="fully-booked">{statusLabel}</p>}
  //       {isFullBooked && breakout.waitlist && (
  //         <p className="fully-booked">
  //           <EventLabel groupName="CMS.FormEvent" name="AddToWaitlist" />
  //         </p>
  //       )}
  //       <EventDetails environment={environment} statusLabel={statusLabel} activeDisplaySettings={activeDisplaySettings} required={required} price={price} />
  //     </div>
  //   </>
  // );
});

// #abstraction-overhaul
// const SelectorComponent = memo(() => {
//   if (isFetching || isFetchingVouchers) {
//     return <Spinner />;
//   }

//   if (displayYesNo) {
//     return (
//       <YesNoSelector
//         {...props}
//         isChecked={isChecked}
//         isFullBooked={isFullBooked}
//         readonly={readonly}
//         isFetching={isFetching}
//         isFetchingVouchers={isFetchingVouchers}
//         checkAvailableStatus={checkAvailableStatus}
//         eventRejected={eventRejected}
//       />
//     );
//   }

//   if (showQuantity && !isFullBooked && !readonly) {
//     return <EventQuantizer sqlStateNo={props.sqlStateNo} eventId={eventId} order={order} quantity={quantity} onChange={handleQuantityChange} />;
//   }

//   return (
//     <Checkbox
//       disabled={(isFullBooked && !isChecked) || readonly || isDue}
//       onChange={e => {
//         checkAvailableStatus(e.target.checked);
//         runInAction(() => {
//           props.userInteracted = true;
//         });
//       }}
//       style={{ opacity: statusLabel || readonly ? 0.3 : 1, cursor: statusLabel ? 'not-allowed' : 'pointer' }}
//       icon={
//         props.hasParent && settings.displayType === 'radio' ? (
//           <RadioButtonUncheckedIcon style={{ color: isValid === false ? '#d53443' : undefined }} className="checkbox cms-checkbox" />
//         ) : (
//           <CheckBoxOutlineBlankIcon className="checkbox cms-checkbox" style={{ color: isValid === false ? '#d53443' : undefined }} />
//         )
//       }
//       checkedIcon={
//         props.hasParent && settings.displayType === 'radio' ? (
//           <RadioButtonCheckedIcon className="checkbox cms-checkbox" />
//         ) : (
//           <CheckBoxIcon className="checkbox cms-checkbox" />
//         )
//       }
//       checked={isChecked}
//       className={classes.checkbox + ' checkbox'}
//       name="checkedA"
//     />
//   );
// }, [
//   isFetching,
//   isFetchingVouchers,
//   displayYesNo,
//   isChecked,
//   isFullBooked,
//   readonly,
//   showQuantity,
//   order,
//   quantity,
//   handleQuantityChange,
//   checkAvailableStatus,
//   eventRejected,
// ]);
